import { type StaticImageData } from "next/image";

import builderKitAnimation from "@assets/images/builder_kit/builder-kit-animation.webm";
import builderKitPoster from "@assets/images/builder_kit/builder-kit.png";
import logoVerge from "@assets/images/homepage/press/press-logo-verge.avif";
import logoWired from "@assets/images/homepage/press/press-logo-wired.svg?url";
import buyProduct from "@assets/images/homepage/redesign/buy-product.webp";
import madeInUsaFlag from "@assets/images/product_page/USFlag.png";
import accessoryBrushRoll from "@assets/images/shop/accessories/brush-roll.webp";
import accessoryDecloggingTool from "@assets/images/shop/accessories/declogging-tool.webp";
import accessoryDescalingPouch from "@assets/images/shop/accessories/descaling-pouch.webp";
import accessoryDock from "@assets/images/shop/accessories/dock.webp";
import accessoryHardwoodCleaner from "@assets/images/shop/accessories/hardwood-cleaner.webp";
import accessoryHepaBags from "@assets/images/shop/accessories/hepa-bags.webp";
import accessoryMatic from "@assets/images/shop/accessories/matic.webp";
import { config } from "@/config";
// TODO(agency): mop-roll render is a busy studio still; swap for an isolated part shot.
import accessoryMopRoll from "@assets/images/shop/accessories/mop-roll.webp";
import accessorySideBrush from "@assets/images/shop/accessories/side-brush.webp";
import accessoryWaterTank from "@assets/images/shop/accessories/water-tank.webp";
import innovationBannerDesktop from "@assets/images/shop/innovation-banner-desktop.webp";
import innovationBannerMobile from "@assets/images/shop/innovation-banner-mobile.webp";
import innovationBannerSd from "@assets/images/shop/innovation-banner-sd.webp";
import innovationBannerTablet from "@assets/images/shop/innovation-banner-tablet.webp";
import overviewDesignPoster from "@assets/images/shop-overview/matic-overview-design-poster.jpg";
import maticInTahoe from "@assets/images/shop/matic-in-tahoe.webp";
import maticWithDog from "@assets/images/shop/matic-with-dog.webp";
import maticAtTheMet from "@assets/images/shop/matic-at-the-met.webp";
import reviewTestimonialChandan from "@assets/images/shop/review-testimonial-chandan.webp";
import reviewTweetShane from "@assets/images/shop/review-tweet-shane.webp";
import socialGoldenGate from "@assets/images/shop/social-golden-gate.webp";
import supportMaticTopdown from "@assets/images/shop/support-matic-topdown.webp";
import supportTeam from "@assets/images/shop/support-team.webp";
import whatsInsideFlatlay from "@assets/images/shop/whats-inside-flatlay.webp";
import { type Hotspot } from "@/blueprint/components/HotspotImage/HotspotImage";
import { FOOTER_STATS } from "@/chrome/chromeContent";
import type {
  ConfiguratorBlockId,
  ConfiguratorSlideId,
  FaqGroupId,
  FaqQuestionId,
  WhatsInsideItemId,
} from "@/analytics/events";
import { REVIEW_AVERAGE, REVIEW_COUNT } from "@/consts/reviews";
import { SITE_LINKS } from "@/data/siteLinks";
import { TESTIMONIALS, type TestimonialTabId } from "@/views/home/homeContent";
import {
  FLOOR_PACKAGES,
  type FloorPackageId,
} from "@/views/shop/floorPackages";

export const SHOP_PAGE = {
  title: "Buy Matic",
};

/** The visual zone's carousel controls. */
export const MATIC_GALLERY = {
  label: "Matic gallery",
  previous: "Previous view",
  next: "Next view",
};

export type MaticModelId = (typeof CONFIGURATOR.models)[number]["id"];

/**
 * The spec bar above the model cards (after Tesla's configurator): three
 * facts, label over value, in one row. Only the noise level differs between
 * the models, so it alone is per-model; cleaning and assembly are the same
 * for both. Trial and warranty are the same for both too, so they sit with
 * the assurances instead.
 */
export const MODEL_SPEC_BAR = {
  label: "Model specs",
  noiseLabel: "Noise level",
  cleaningLabel: "Cleaning",
  cleaning: "Vacuum + Mop",
  assemblyLabel: "Assembly",
  assembly: "California",
  // TODO(claim): Serene's 52 dB needs checking against engineering's noise
  // chart. Merge blocker.
  noiseDb: {
    matic: 55,
    "matic-serene": 52,
  } satisfies Record<MaticModelId, number>,
  noiseValue: (db: number) => `${db} dB`,
} as const;

/**
 * The ways to pay, as the tabs over the options column, after /lp/pet (its
 * landingPaymentContent, word for word): the Add to cart keeps its words,
 * the bars show the total or the 0% APR monthly with a line on how to
 * pay, and the cart is stamped with it. Shopify's checkout can't be told the
 * method; the buyer still picks it there. The label is for screen readers.
 */
export const PAYMENT_TABS = {
  label: "Payment options",
  /*
   * Each tab leads with the marks shoppers know, as Eight Sleep's do: the
   * label is the tab's name; `text` is what shows beside the marks (Affirm's
   * logo says "Affirm", so its text is just "Finance").
   */
  tabs: [
    { id: "now", label: "Pay now", text: "Pay now" },
    { id: "finance", label: "Affirm finance", text: "Finance" },
    { id: "hsa", label: "HSA/FSA", text: "HSA/FSA" },
  ],
  /** The cart attribute /lp/pet stamps too, so orders compare across pages. */
  cartAttribute: "preferred_payment_method",
  stickyTagline: {
    now: "Pay in full at checkout",
    finance: "View installment options at checkout",
    hsa: "Pay with HSA/FSA at checkout",
  },
  /** The bars' monthly quote: the lead small, the figure at full size. */
  monthlyLead: "From",
  monthlyPrice: (price: string) => `${price}/mo`,
  /** Months when Shopify sends no plans (the dev store), as /lp/pet's. */
  fallbackMonths: 6,
} as const;

export type PaymentTabId = (typeof PAYMENT_TABS.tabs)[number]["id"];

/** A line over a film from `from` seconds; null where the film says it alone. */
export type FilmMoment = { from: number; text: string | null };

/**
 * The moments of a film that carries itself: silent from the first frame,
 * so no caption runs over it, where a clip without moments (null) would
 * fall back to its bullet or the tab's title.
 */
export const UNCAPTIONED_FILM: readonly FilmMoment[] = [
  { from: 0, text: null },
];

/**
 * The Overview film, shared by both models: the design film (MaticDesign_v17a),
 * from 1.25s (past its logo title) to the shot change at 15.9s, silent,
 * 1080p, 14.7s, 3.0 MB; poster at 1.25s. The film carries itself: no
 * running caption over it.
 */
export const OVERVIEW_FILM = {
  video: `${config.media.baseUrl}/videos/matic-overview-design.mp4`,
  poster: overviewDesignPoster,
  alt: "Matic at home: on rugs and hardwood, beside the family and the dog",
  moments: UNCAPTIONED_FILM,
};

/**
 * What the configurator's visual zone shows for each options block, Tesla's
 * scroll-linked pattern: the block crossing the trigger line drives the zone,
 * which crossfades to that block's set and opens on its first slide. Blocks
 * are listed in page order; a block whose set comes up empty (no imagery
 * loaded) keeps the one before it.
 */
export const CONFIGURATOR_VISUALS = {
  /** The zone's pause button, over any film or clip; as the landing pages'. */
  videoToggle: { pause: "Pause video", play: "Play video" },
  blocks: [
    // The title and model blocks show the Overview tab's film;
    // FEATURE_TABS (featureTabsContent) adds the other tabs, the 3D model
    // ("orbit") under Matic Design among them.
    {
      id: "title",
      slides: ["overview-film"],
      announce: "Showing Matic",
    },
    {
      id: "model",
      slides: ["overview-film"],
      announce: "Showing Matic",
    },
    // The live renders: the robot count animates as the combination changes.
    {
      id: "combination",
      slides: ["setup"],
      announce: "Showing your combination",
    },
    {
      id: "addOns",
      // In the add-ons' page order; Bag Pass and Matic Care play their
      // clips (ADD_ON_CLIPS in featureTabsContent).
      slides: [
        "annual-bag-pass",
        "matic-care",
        "builder-kit",
        "pet-brush-roll",
      ],
      announce: "Showing add-ons",
    },
    // TODO(asset): a final-configuration image; the renders stand in.
    {
      id: "checkout",
      slides: ["setup"],
      announce: "Showing your configuration",
    },
  ] as const satisfies readonly {
    id: ConfiguratorBlockId;
    slides: readonly ConfiguratorSlideId[];
    announce: string;
  }[],
  slideLabels: {
    orbit: "360°",
    "overview-film": "Overview",
    setup: "Your setup",
    "annual-bag-pass": "Annual Bag Pass",
    "matic-care": "Matic Care",
    "builder-kit": "Builder Kit",
    "pet-brush-roll": "Pet Brush Roll",
  } satisfies Record<ConfiguratorSlideId, string>,
  builderKit: {
    video: builderKitAnimation,
    poster: builderKitPoster,
  },
  petBrushRollAlt: "The Pet Brush Roll, lined with felt for pet hair",
} as const;

/**
 * The add-on details dialog (after Tesla's accessory dialogs): each add-on's
 * name, photo and description come from Shopify; only its controls live here.
 */
export const ADD_ON_DETAILS = {
  // The dialog selects the add-on, as its card does; the cart is the CTA's.
  add: "Add",
  remove: "Remove",
  close: "Close",
  previous: "Previous add-on",
  next: "Next add-on",
  show: (name: string) => `Show ${name}`,
  pagination: "Add-ons",
};

export type { ConfiguratorBlockId, ConfiguratorSlideId };

type ConfiguratorOption = {
  id: FloorPackageId;
  label: string;
  note: readonly string[];
};

export const CONFIGURATOR = {
  title: "Configure your Matic",
  reviewAverage: REVIEW_AVERAGE,
  reviewCount: REVIEW_COUNT,
  modelHeading: "Choose your Matic",
  // Same hardware, so the cards carry the difference in words: `tagline` is
  // the one-line differentiator, `shippingNote` the first ship date, under
  // the card's price and the bars' model line.
  models: [
    {
      id: "matic",
      title: "Matic",
      tagline: "Quiet by design",
      shippingNote: null,
    },
    {
      id: "matic-serene",
      title: "Matic Serene",
      tagline: "Even smoother sound",
      shippingNote: "Ships by early December",
    },
  ] as const,
  combosHeading: "Which combination fits your home?",
  // Add-on names and descriptions are authored in Shopify, so only the group
  // heading lives here.
  addOnsHeading: "Add-Ons",
  addOnsLearnMore: "Learn more",
  bundleBonusLabel: (maticCount: number) =>
    `Free Annual Bag Pass (${maticCount}x)`,
  includedLabel: "Included",
  freePriceLabel: "free",
  perMaticNote: "per Matic",
  trialNotice: "6-month risk-free trial",
  combos: FLOOR_PACKAGES.map(({ id, title, description }) => ({
    id,
    label: title,
    note: description,
  })) satisfies readonly ConfiguratorOption[],
  ctaLabel: "Add to cart",
  /** Names the assurance strip over the visual zone, for screen readers. */
  assurancesLabel: "Our promises",
  /* Two authored lines each; the sticky bar joins them with a space. */
  assurances: [
    { id: "risk-free", lines: ["Try 6 months", "risk-free"] },
    { id: "shipping", lines: ["Ships in", "2 weeks"] },
    { id: "warranty", lines: ["1-year", "warranty"] },
    { id: "returns", lines: ["Free returns", "(contiguous U.S.)"] },
  ] as const,
  /** In place of "Ships in 2 weeks" under Add to cart, while Serene is picked. */
  sereneShipping: {
    id: "serene-shipping",
    lines: ["Serene ships", "by early December"],
  } as const,
  eligibilityBadge: {
    label: "HSA/FSA payment options now available",
  },
  eligibility: {
    dialogLabel: "How HSA/FSA eligibility works",
    closeLabel: "Close",
    heading: "Use your HSA/FSA for Matic",
    steps: [
      "At checkout, choose Flex | Pay with HSA/FSA.",
      "Complete a brief, chat-based telehealth consultation to confirm eligibility and receive a Letter of Medical Necessity.",
      "If eligible, pay with your HSA/FSA card, or another payment method and submit for reimbursement.",
    ],
    footnoteLead:
      "Eligible customers can use HSA/FSA funds for Matic and its accessories, including the Annual Bag Pass.",
    footnote: "Matic Care and stickers require another payment method.",
  },
};

type WhatsInsideItem = {
  id: WhatsInsideItemId;
  image: StaticImageData;
  count: string;
  name: string;
  description: string;
  detailHeading?: string;
  details: readonly { label?: string; value: string }[];
};

type WhatsInsideHotspot = Omit<Hotspot, "id"> & { id: WhatsInsideItemId };

export const WHATS_INSIDE = {
  eyebrow: "What's inside",
  title: "Ready to roll, out of the box",
  image: whatsInsideFlatlay,
  imageAlt: "Matic and its included accessories arranged in a flat lay",
  items: [
    {
      id: "matic",
      image: accessoryMatic,
      count: "1x",
      name: "Matic",
      description:
        "The most advanced floor cleaning robot. Lovable, compact, and ultra-quiet.",
      detailHeading: "Dimensions",
      details: [
        {
          label: "Body",
          value: 'Length: 11", Width: 9.4", Height: 7.8"',
        },
        {
          label: "Cleaning Head",
          value: 'Length: 4.1", Width: 9.4", Height: 3.0"',
        },
      ],
    },
    {
      id: "dock",
      image: accessoryDock,
      count: "1x",
      name: "Dock",
      description:
        "Matic’s home base. Set it up in a well-lit area with at least 1 foot of clearance on each side and overhead, and 3 feet from stairs.",
      detailHeading: "Dimensions",
      details: [
        {
          label: "Body",
          value: 'Width: 4.9", Length: 7.1", Height: 5.1"',
        },
      ],
    },
    {
      id: "hepa-bags",
      image: accessoryHepaBags,
      count: "5x",
      name: "HEPA Bags",
      description:
        "Captures wet and dry debris, including fine dust and allergens. Typically lasts 1–4 weeks, depending on home size and cleaning frequency.",
      details: [{ value: "1 pre-installed" }, { value: "4 extra" }],
    },
    {
      id: "mop-roll",
      image: accessoryMopRoll,
      count: "2x",
      name: "Mop Roll",
      description:
        "Advanced microfiber scrubs away sticky stains, dirt, and grime. Lasts 1–3 months, depending on mopping frequency.",
      details: [{ value: "1 pre-installed" }, { value: "1 extra" }],
    },
    {
      id: "brush-roll",
      image: accessoryBrushRoll,
      count: "1x",
      name: "Brush Roll",
      description:
        "Tangle-free design helps prevent hair, dander, and dust buildup. Replace when worn down to the indicator, typically after 5 months.",
      details: [{ value: "1 pre-installed" }],
    },
    {
      id: "water-tank",
      image: accessoryWaterTank,
      count: "1x",
      name: "Water Tank",
      description:
        "Holds the water Matic uses to mop your floors. Matic sends an alert when it’s empty to keep your mopping routine running smoothly.",
      details: [],
    },
    {
      id: "declogging-tool",
      image: accessoryDecloggingTool,
      count: "1x",
      name: "Declogging Tool",
      description:
        "For the rare occasion when Matic’s vacuum duct needs clearing. Matic will alert you if it’s needed.",
      details: [],
    },
    {
      id: "side-brush",
      image: accessorySideBrush,
      count: "3x",
      name: "Side Brush",
      description:
        "Sweeps dust and debris from edges, corners, and other hard-to-reach areas. Replace when the bristles fray, typically after 1–2 months.",
      details: [{ value: "1 pre-installed" }, { value: "2 extra" }],
    },
    {
      id: "descaling-pouch",
      image: accessoryDescalingPouch,
      count: "2x",
      name: "Descaling Pouch",
      description:
        "Dissolves in the water tank to clear Matic’s tubing and maintain water flow. Matic will alert you if descaling is needed.",
      details: [],
    },
    {
      id: "hardwood-cleaner",
      image: accessoryHardwoodCleaner,
      count: "2x",
      name: "Aunt Fannie's Floor Cleaner Sample",
      description:
        "Plant-based floor cleaner for mopping with Matic. Kid- and pet-friendly, it helps neutralize odors while leaving floors fresh and clean. Use 1 packet per tank.",
      details: [],
    },
  ] satisfies readonly [WhatsInsideItem, ...WhatsInsideItem[]],
  // Button centers from the Figma "What's In the Box" Desktop graphic
  // (10583-55465). Figma positions the markers inside a 924x673 box that sits
  // at (269, 165) within the 1392x892.785 flat-lay, and WhatsInside renders the
  // image's full height, so percentages are image-relative:
  //   x = (nodeX + 21.5) / 924 * 100        (box and image share a left edge
  //                                          offset, so x is unchanged)
  //   y = (nodeY + 21.5 + 165) / 892.785 * 100
  // Figma reports a marker's top-left, hence the +21.5 at the 43px marker size.
  // Small Desktop (10583-55403, 759x553) agrees within ~1pt. Spots are
  // item-adjacent, not centered on each item.
  //
  // Dock is the one exception and has been since the markers were first laid
  // out: its Desktop node sits at y=49, which is low on the dock body, so it
  // takes Small Desktop's placement instead — nodeY=18, giving 22.9 rather
  // than the 26.4 the Desktop node yields. Deriving it from the Desktop node
  // will move it; that is a regression, not a correction.
  hotspots: [
    { id: "matic", x: 48.4, y: 74.8, label: "Show Matic details" },
    { id: "dock", x: 11.9, y: 22.9, label: "Show Dock details" },
    {
      id: "hepa-bags",
      x: 78.2,
      y: 48.6,
      label: "Show HEPA Bags details",
    },
    {
      id: "mop-roll",
      x: 11.9,
      y: 45.1,
      label: "Show Mop Roll details",
    },
    {
      id: "brush-roll",
      x: 11.9,
      y: 53.8,
      label: "Show Brush Roll details",
    },
    {
      id: "water-tank",
      x: 70.2,
      y: 34.6,
      label: "Show Water Tank details",
    },
    {
      id: "declogging-tool",
      x: 11.9,
      y: 67.0,
      label: "Show Declogging Tool details",
    },
    {
      id: "side-brush",
      x: 40.3,
      y: 62.0,
      label: "Show Side Brush details",
    },
    {
      id: "descaling-pouch",
      x: 38.7,
      y: 77.8,
      label: "Show Descaling Pouch details",
    },
    {
      id: "hardwood-cleaner",
      x: 18.2,
      y: 77.8,
      label: "Show Aunt Fannie's Floor Cleaner Sample details",
    },
  ] satisfies readonly WhatsInsideHotspot[],
} as const;

export const MADE_IN_USA = {
  title: "Designed and assembled in Mountain View, California.",
  body: "Making Matic in America means a lot to us. While Matic's components come from around the globe, every Matic is assembled with care in California by our small, dedicated team.",
  image: madeInUsaFlag,
  imageAlt: "Flag of the United States",
} as const;

export type ReviewWallCard =
  | {
      id: string;
      kind: "press";
      quote: string;
      logo: StaticImageData | string;
      logoAlt: string;
    }
  | { id: string; kind: "rating" }
  | {
      id: string;
      kind: "tweet";
      image: StaticImageData;
      imageAlt: string;
      author: string;
      handle: string;
      quote: string;
    }
  | ReviewWallTestimonialCard
  | {
      id: string;
      kind: "stat";
      stats: readonly [ReviewWallStat, ...ReviewWallStat[]];
      footnote: string;
    };

export type ReviewWallStat = {
  id: string;
  prefix: string;
  // Rendered flush against the counter (e.g. "$"), unlike the spaced unit.
  valuePrefix: string;
  srLabel: string;
  unit: string;
  suffix: string;
  seedValue: number;
  perSecond: number;
  seedTimeMs: number;
};

export type ReviewWallTestimonialCard = {
  id: string;
  kind: "testimonial";
  eyebrow: string;
  storySubject: string;
  quote: string | null;
  poster: StaticImageData | null;
  posterAlt: string;
  videoSrc: string | null;
};

export type ReviewWallTabId = "general-love" | TestimonialTabId;

const getHomeTestimonial = (id: TestimonialTabId) => {
  const testimonial = TESTIMONIALS.find((item) => item.id === id);
  if (testimonial === undefined) {
    throw new Error(`Missing home testimonial: ${id}`);
  }
  return testimonial;
};

const makeCohortTestimonial = (
  id: TestimonialTabId,
  fallbackEyebrow: string,
): ReviewWallTestimonialCard => {
  const testimonial = getHomeTestimonial(id);
  const eyebrow = [testimonial.attribution, testimonial.detail]
    .filter((value) => value !== "")
    .join(" / ");

  return {
    id: `testimonial-${id}`,
    kind: "testimonial",
    eyebrow: eyebrow === "" ? fallbackEyebrow : eyebrow,
    storySubject:
      testimonial.attribution === ""
        ? fallbackEyebrow
        : testimonial.attribution,
    quote: testimonial.quote === "" ? null : testimonial.quote,
    // The review card is portrait at every breakpoint; the portrait cut crops best.
    poster: testimonial.posterMobile,
    posterAlt: testimonial.posterAlt,
    videoSrc: testimonial.video,
  };
};

const cleanedSqFtStat = FOOTER_STATS.find(
  (stat) => stat.id === "sq-ft-cleaned",
);
const weekendsSavedStat = FOOTER_STATS.find(
  (stat) => stat.id === "weekends-saved",
);

if (cleanedSqFtStat === undefined || weekendsSavedStat === undefined) {
  throw new Error("Missing footer stats for the review wall stat card");
}

export const REVIEW_WALL = {
  title: "Loved, in every type of home",
  seeMoreLabel: "See more reviews",
  tabs: [
    {
      id: "general-love",
      label: "General Love",
      testimonial: {
        id: "testimonial-general-love",
        kind: "testimonial",
        eyebrow: "Chandan / With Matic Since 2023",
        storySubject: "Chandan",
        quote: null,
        poster: reviewTestimonialChandan,
        posterAlt: "Chandan sharing his experience with Matic",
        // TODO(agency): Chandan's cut doubles as General Love and Families until
        // a dedicated general-love testimonial is delivered.
        videoSrc: `${config.media.baseUrl}/videos/testimonial-families-chandan-v2.mp4`,
      },
    },
    {
      id: "pets",
      label: "Pet Owners",
      testimonial: makeCohortTestimonial("pets", "Pet Owners"),
    },
    {
      id: "families",
      label: "Families",
      testimonial: makeCohortTestimonial("families", "Families"),
    },
    {
      id: "work-from-home",
      label: "Busy Homes",
      // TODO(agency): this category still needs the brief's additional
      // testimonial image/video review and separate text review.
      testimonial: makeCohortTestimonial("work-from-home", "Busy Homes"),
    },
    {
      id: "time-saved",
      label: "Time Saved",
      testimonial: makeCohortTestimonial("time-saved", "Time Saved"),
    },
  ],
  sharedCards: {
    wired: {
      id: "press-wired",
      kind: "press",
      quote:
        "The only 10/10 rating I have ever given in over a decade of reviewing products.",
      logo: logoWired,
      logoAlt: "WIRED",
    },
    rating: {
      id: "rating",
      kind: "rating",
    },
    tweet: {
      id: "tweet-shane",
      kind: "tweet",
      image: reviewTweetShane,
      imageAlt: "Matic cleaning Shane Parrish's wood floor",
      author: "Shane Parrish ✓",
      handle: "@ShaneParrish",
      quote: "Best product I've seen in years @maticrobots. Simply incredible.",
    },
    stat: {
      id: "stat-square-feet",
      kind: "stat",
      stats: [
        {
          id: "sq-ft-cleaned",
          prefix: "Matic has cleaned",
          valuePrefix: "",
          srLabel: cleanedSqFtStat.srLabel,
          unit: "sq ft",
          suffix: "and counting.",
          seedValue: cleanedSqFtStat.seedValue,
          perSecond: cleanedSqFtStat.perSecond,
          seedTimeMs: cleanedSqFtStat.seedTimeMs,
        },
        {
          id: "weekends-saved",
          prefix: "Matic has saved",
          valuePrefix: "",
          srLabel: weekendsSavedStat.srLabel,
          unit: "weekends",
          suffix: "and counting.",
          seedValue: weekendsSavedStat.seedValue,
          perSecond: weekendsSavedStat.perSecond,
          seedTimeMs: weekendsSavedStat.seedTimeMs,
        },
        // TODO(matic): verify the three invented stats below against real telemetry.
        {
          id: "family-hours",
          prefix: "Matic has given back",
          valuePrefix: "",
          srLabel: "over 3 million",
          unit: "hours",
          suffix: "of family time.",
          seedValue: Math.round(weekendsSavedStat.seedValue * 48),
          perSecond: weekendsSavedStat.perSecond * 48,
          seedTimeMs: weekendsSavedStat.seedTimeMs,
        },
        {
          id: "messes-handled",
          prefix: "Matic has cleaned up",
          valuePrefix: "",
          srLabel: "over 3 million",
          unit: "messes",
          suffix: "without being asked.",
          seedValue: 3_164_000,
          perSecond: 0.5,
          seedTimeMs: weekendsSavedStat.seedTimeMs,
        },
        {
          id: "cleaning-costs-saved",
          prefix: "Matic has saved households",
          valuePrefix: "$",
          srLabel: "over $12 million",
          unit: "",
          suffix: "in professional cleaning costs.",
          // ~$170, the going rate for one hired cleaning visit, per weekend saved.
          seedValue: Math.round(weekendsSavedStat.seedValue * 170),
          perSecond: weekendsSavedStat.perSecond * 170,
          seedTimeMs: weekendsSavedStat.seedTimeMs,
        },
      ],
      footnote: "Another stat",
    },
    verge: {
      id: "press-verge",
      kind: "press",
      quote:
        "This WALL-E-like bot fixes the stuff every other robot vacuum gets wrong.",
      logo: logoVerge,
      logoAlt: "The Verge",
    },
  } satisfies Record<string, ReviewWallCard>,
  rating: {
    average: REVIEW_AVERAGE,
    count: REVIEW_COUNT,
  },
} as const;

export const SHOP_PRODUCT_IMAGE = {
  image: buyProduct,
  alt: "Matic, top view",
};

export const INNOVATION_BANNER = {
  eyebrow: "Innovation",
  title: "A new era of intelligent cleaning",
  href: "/innovation",
  // Per-breakpoint crops of the rear render, baked from the Figma banner's
  // image transforms — each breakpoint frames the camera bar differently.
  images: {
    mobile: innovationBannerMobile,
    tablet: innovationBannerTablet,
    smallDesktop: innovationBannerSd,
    desktop: innovationBannerDesktop,
  },
  imageAlt: "The cameras on Matic's rear sensor bar, up close",
};

export type ShopFaqItem = {
  id: FaqQuestionId;
  question: string;
  /** Opening sentence, set in a heavier weight ahead of the answer. */
  lead: string;
  answer: readonly ShopFaqParagraph[];
};

/**
 * A later paragraph may open with its own heavier-weight phrase, or close on
 * a link.
 */
export type ShopFaqParagraph =
  | string
  | { lead: string; text: string }
  | { text: string; link: { label: string; href: string } };

/** The paragraph as plain prose; a link's label is left out. */
export const shopFaqParagraphText = (paragraph: ShopFaqParagraph): string =>
  typeof paragraph === "string"
    ? paragraph
    : "lead" in paragraph
      ? `${paragraph.lead} ${paragraph.text}`
      : paragraph.text;

export type ShopFaqGroup = {
  id: FaqGroupId;
  title: string;
  items: readonly ShopFaqItem[];
};

export const SHOP_FAQS = {
  eyebrow: "Frequently Asked Questions",
  title: "Your Questions, Answered",
  groups: [
    {
      id: "living-with-matic",
      title: "Living with Matic",
      items: [
        {
          id: "multi-floor-house",
          question: "Does Matic work in multi-floor homes?",
          lead: "Yes! Matic can map multiple floors and automatically recognize where it is.",
          answer: [
            "Simply carry it between floors. Since Matic can’t climb stairs, you can return it to one dock to charge, add a second dock, or keep a Matic on each floor.",
          ],
        },
        {
          id: "climb-ramps",
          question: "Can Matic climb stairs, ramps, or raised thresholds?",
          lead: "Matic’s large wheels help it move smoothly between hard floors and rugs and cross thresholds up to 0.9″.",
          answer: [
            "Matic can’t climb stairs. Ramps aren’t officially supported; while Matic has climbed ramps under 18° in testing, we can’t guarantee reliable performance.",
          ],
        },
        {
          id: "clean-in-the-dark",
          question: "Can Matic clean in the dark or at night?",
          lead: "Yes! Matic’s infrared cameras help it see in low light and darkness, and it’s quiet enough to clean overnight.",
          answer: [
            "When Matic first gets to know your home, leave the lights on so it can build the best map. Matic maps just fine with lights out but the colors will look less lively.",
          ],
        },
        {
          id: "battery-life",
          question: "How long does Matic run on a charge?",
          lead: "Up to 2.5 hours while vacuuming or 3 hours while mopping.",
          answer: [
            "If the job takes longer, Matic returns to its dock, recharges, and picks up where it left off.",
          ],
        },
        {
          id: "quiet",
          question: "How quiet is Matic?",
          lead: "Matic operates at about 55 dB while vacuuming, quieter than a typical conversation.",
          answer: [
            "Matic Serene brings that down to approximately 52 dB, with a softer, smoother sound that makes cleaning even less noticeable.",
            "Both models operate at about 52 dB while mopping and 50 dB while patrolling.",
          ],
        },
        {
          id: "safe-around-kids-and-pets",
          question: "Is Matic safe around kids and pets?",
          lead: "Yes! Matic is designed to share the floor.",
          answer: [
            "It recognizes people and pets and gives them the right-of-way, with rounded corners, quiet movement, and no exposed spinning side brushes.",
          ],
        },
      ],
    },
    {
      id: "maintenance",
      title: "Maintenance",
      items: [
        {
          id: "refill-water-tank",
          question: "How often do I refill the water tank?",
          lead: "A full 600 mL tank typically mops about 700 square feet in a standard run.",
          answer: [
            "If you mop a room or two a day, expect to refill about once or twice a week. If you mop most of your home daily, plan to refill every day. How long a full tank lasts will depend on your home’s layout and how much hard floor Matic covers.",
          ],
        },
        {
          id: "self-cleaning-mop",
          question: "Does Matic’s mop clean itself?",
          lead: "Yes, continuously while mopping.",
          answer: [
            "Clean water goes onto the roll while a built-in wringer removes dirty water and sends it into the HEPA bag, so Matic lifts dirt instead of spreading it around.",
          ],
        },
        {
          id: "replace-hepa-bag",
          question: "How often do I replace the HEPA bag?",
          lead: "About every 2 weeks.",
          answer: [
            "Depending on the size of your home and how often Matic cleans, a bag may last longer or fill in as little as a week. Our Annual Bag Pass lets you order replacement bags whenever you need them for one yearly price.",
          ],
        },
        {
          id: "replace-rolls",
          question: "When do I replace the brush and mop rolls?",
          lead: "A mop roll typically lasts 1–3 months, and a brush roll about five months.",
          answer: [
            "Actual timing depends on use, and the brush roll’s wear indicator helps take out the guesswork.",
          ],
        },
      ],
    },
    {
      id: "app-and-privacy",
      title: "App & Privacy",
      items: [
        {
          id: "iphone-and-android",
          question: "Can I use the Matic app on iPhone and Android?",
          lead: "Yes!",
          answer: [
            "The Matic app is available for iPhone, iPad, and Android. Use it to schedule cleans, choose where Matic cleans, set no-go zones, and check in from anywhere.",
          ],
        },
        {
          id: "privacy-and-security",
          question: "How does Matic keep my home and data private?",
          lead: "Matic does its seeing and thinking on the robot.",
          answer: [
            "Camera feeds and maps stay on Matic by default instead of being stored in the cloud. Remote connections are encrypted so even Matic can’t see what passes between your phone and robot. If you turn on Matic Cues, only what you say after “Hey Matic” is sent in encrypted form to Google Gemini to interpret your command. You choose if and when to share usage data and diagnostics with us.",
          ],
        },
      ],
    },
    {
      id: "ownership-and-support",
      title: "Owning Matic",
      items: [
        {
          id: "warranty-and-returns",
          question: "What are Matic’s warranty and return policies?",
          lead: "Matic comes with a one-year limited warranty. You can also try Matic for six months and return it for a full refund if it isn’t right for your home.",
          answer: [
            "Matic Care extends warranty coverage to two years and adds accidental damage coverage. Terms apply.",
          ],
        },
        {
          id: "international-shipping",
          question: "Does Matic ship outside the U.S.?",
          lead: "Not yet.",
          answer: [
            "Matic currently ships only within the United States. While Matic supports dual voltage, we’re still working through the compliance and approvals needed for other regions. We don’t have a confirmed timeline yet, but expanding globally is part of our plan.",
            "Some international customers have Matic shipped to a U.S. address, then use a reshipping service to forward it. Matic’s warranty is valid only where Matic is officially sold. If you live outside the U.S. and you’re interested in purchasing Matic, please join our mailing list for updates.",
          ],
        },
      ],
    },
    {
      id: "matic-serene",
      title: "Matic Serene",
      items: [
        {
          id: "serene-sound",
          question: "What makes Matic Serene sound different?",
          lead: "Serene isn’t just quieter at 52 dB.",
          answer: [
            {
              text: "Its redesigned vacuum stack reduces prominent high-frequency tones, softening the sharper whine that can stand out during cleaning. The result is a smoother, more even sound that’s gentler on the ears.",
              link: {
                label: "Learn more →",
                href: "/blog/engineering-behind-matic-serene",
              },
            },
          ],
        },
        {
          id: "serene-cleaning",
          question: "Does Serene clean differently from Matic?",
          lead: "Not at all.",
          answer: [
            "Serene has the same intelligent navigation, powerful vacuuming, and self-cleaning mop as Matic. The difference is in the sound: a redesigned vacuum stack makes Serene our quietest Matic yet.",
          ],
        },
        {
          id: "serene-upgrade",
          question: "Can I upgrade my current Matic to Serene?",
          lead: "Yes!",
          answer: [
            "Existing Matic owners can reserve a Serene upgrade now by contacting our Support team and paying the difference between their original Matic purchase price and Serene’s current price of $1,695.",
            {
              lead: "This is not a trade-in program.",
              text: "We’ll bring your existing Matic up to Serene-level performance, upgrading the vacuum stack and any other components needed to get there. Upgrades begin February 1, 2027, with limited availability on a first-come, first-served basis.",
            },
            "The upgraded vacuum stack comes with a six-month limited warranty.",
            {
              lead: "Please note:",
              text: "If you return your Matic under our standard return policy, we understand that it may not be the right fit for your home. As a result, you won’t be eligible to purchase another Matic, including Matic Serene, for 12 months from the date of your return.",
            },
          ],
        },
      ],
    },
  ] satisfies readonly ShopFaqGroup[],
};

export const SUPPORT_CARDS = {
  roadmap: {
    title: "Your Matic gets smarter",
    body: "After its arrival home, your Matic continually receives new features through regular software updates.",
    ctaLabel: "See the roadmap",
    href: "/innovation#innovation-roadmap",
    image: supportMaticTopdown,
    imageAlt: "Matic docked, viewed from above",
  },
  team: {
    title: "Built and supported by us",
    body: "We design, test, assemble, and support our products here in the US. If you ever need help, you'll talk to our US-based support team, who know Matic inside and out.",
    ctaLabel: "Our story",
    href: "/company",
    image: supportTeam,
    imageAlt: "The Matic support team working together",
  },
};

export type SocialNetworkId = "x" | "instagram" | "linkedin";

export const SOCIAL_CAROUSEL = {
  eyebrow: "@MATICROBOTS",
  eyebrowHref: "https://x.com/maticrobots",
  title: "Follow Matic on social, not around your house.",
  photos: [
    // TODO(agency): replace the still stack with clean 3–5s social clips
    // without baked text or subtitles once the source footage is delivered.
    {
      id: "golden-gate",
      image: socialGoldenGate,
      alt: "Matic overlooking the Golden Gate Bridge",
    },
    {
      id: "matic-in-tahoe",
      image: maticInTahoe,
      alt: "Matic out by Lake Tahoe",
    },
    {
      id: "matic-at-the-met",
      image: maticAtTheMet,
      alt: "Matic parked on the steps of The Met beside two visitors",
    },
    {
      id: "matic-with-dog",
      image: maticWithDog,
      alt: "Matic on a living room rug beside a Bernese mountain dog",
    },
  ],
  links: [
    { id: "x", ...SITE_LINKS.twitter },
    { id: "instagram", ...SITE_LINKS.instagram },
    { id: "linkedin", ...SITE_LINKS.linkedin },
  ] satisfies readonly ({ id: SocialNetworkId } & (typeof SITE_LINKS)[
    "twitter" | "instagram" | "linkedin"])[],
};
